import { LocaleKey } from '@Locale/locale.ts';
import { OptionType } from '@Models/model.types';
import { AsyncDropdownOptions } from '@tlx/astro-shared';
import {
    DropdownDrawer,
    DropdownField,
    DropdownOpener,
    DropdownScrollContainer,
    DropdownSearch,
    Option,
} from '@tlx/atlas';
import { useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';

export function AttributeDropdown({
    text,
    id,
    options,
    url,
    onChange,
    isRequired,
    disabled,
}: {
    text?: LocaleKey;
    id: string;
    options?: OptionType[];
    url?: string;
    onChange?: (value: string) => void;
    isRequired?: boolean;
    disabled?: boolean;
}) {
    const intl = useIntl();
    const [searchString, setSearchString] = useState<string>('');
    return (
        <DropdownField
            className={'atl-w-full tsk-flex-half'}
            name={id}
            data-testid={id}
            defaultSelectedKey={options?.[0].id}
            isRequired={isRequired}
            label={text ? intl.formatMessage({ id: text }) : undefined}
            onSelectionChange={(value) => {
                if (value === null) {
                    return;
                }
                if (onChange) {
                    onChange(value.toString());
                }
            }}
        >
            <DropdownOpener className="atl-w-full" disabled={disabled} />
            <DropdownDrawer>
                <DropdownSearch onChange={(e) => setSearchString(e.target.value)} />
                <DropdownScrollContainer>
                    {options
                        ?.filter((option) => option.text.toLowerCase().includes(searchString.toLowerCase()))
                        .map(
                            (option) => (
                                <Option key={option.id} value={option.id}>
                                    {option.text}
                                </Option>
                            ),
                            options,
                        )}
                    {url && <AsyncDropdownOptions url={url} />}
                </DropdownScrollContainer>
            </DropdownDrawer>
        </DropdownField>
    );
}

export function getNoChangeOption(intl: IntlShape) {
    return [{ id: '-1', text: intl.formatMessage({ id: 'text_no_change' }) }];
}

export function getNotChosenOption(intl: IntlShape, id = '') {
    return [{ id: id, text: intl.formatMessage({ id: 'option_not_chosen' }) }];
}

export function getPayslipDeliveryMethodOptions(intl: IntlShape) {
    return [
        {
            id: '0',
            text: intl.formatMessage({ id: 'text_delivery_wageslip_local' }),
        },
        {
            id: '3',
            text: intl.formatMessage({ id: 'text_delivery_wageslip_email' }),
        },
        {
            id: '4',
            text: intl.formatMessage({ id: 'text_delivery_wageslip_access' }),
        },
    ];
}

export function getWageSpecificationTemplateTypeOptions(intl: IntlShape) {
    return [
        { id: '2', text: intl.formatMessage({ id: 'text_monthly_addition' }) },
        { id: '1', text: intl.formatMessage({ id: 'text_monthly_deduction' }) },
        {
            id: '4',
            text: intl.formatMessage({
                id: 'text_percentage_calculation_addition',
            }),
        },
        {
            id: '3',
            text: intl.formatMessage({
                id: 'text_percentage_calculation_deduction',
            }),
        },
    ];
}

export function getSystemAccessTemplateOptions(intl: IntlShape) {
    return [
        { id: '0', text: intl.formatMessage({ id: 'text_disable' }) },
        { id: '1', text: intl.formatMessage({ id: 'text_enable' }) },
    ];
}

export function getPersonnelManagerOptions(intl: IntlShape) {
    return [
        { id: '-2', text: intl.formatMessage({ id: 'text_no_change' }) },
        { id: '0', text: intl.formatMessage({ id: 'option_none' }) },
        {
            id: '-1',
            text: `(${intl.formatMessage({ id: 'text_department_manager' })})`,
        },
    ];
}
